import React, { useEffect, useState, useRef, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Globe, Plus, AlertCircle, Instagram, Facebook, Twitter, ChevronLeft, Clock, Users, Award, Image as ImageIcon, ExternalLink, Download, Share2, MessageCircle, ArrowRight } from 'lucide-react';
// Declare Leaflet types
declare global {
  interface Window {
    L: any;
  }
}
interface Event {
  id: number;
  title: string;
  date: string;
  endDate: string;
  displayDate: string;
  displayEndDate: string;
  location: string;
  venue: string;
  venueAddress: string;
  organizer: string;
  description: string;
  image: string;
  heroImage: string;
  websiteUrl: string;
  eventTypes: string[];
  distance: string[];
  details: string[];
  galleryStatus: string;
  galleryImages?: string[];
  raceResultsStatus: string;
  raceResults?: Array<{
    place: number;
    name: string;
    time: string;
    pace: string;
  }>;
  comments?: Array<{
    id: number;
    author: string;
    date: string;
    text: string;
    avatar?: string;
  }>;
  coordinates: {
    lat: number;
    lng: number;
  };
}
export function EventView() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Mock event data - in production this would come from an API
  const allEvents: Event[] = [{
    id: 1,
    title: 'Run, Ride & Rumble For PTSD Awareness - 5K',
    date: '2026-06-13',
    endDate: '2026-06-13',
    displayDate: '06-13-2026 - 08:00 am',
    displayEndDate: '06-13-2026 - 11:00 am',
    location: 'Birmingham, AL',
    venue: 'Cahaba Brewing Company',
    venueAddress: '4500 5th Ave S building c, Birmingham, AL 35222, United States',
    organizer: 'Sojourn Counseling',
    description: "Don't miss Sojourn Counseling's fifth annual RUN, RIDE and RUMBLE for PTSD Awareness on June 13, 2026 at Cahaba Brewing in Birmingham, AL! June is PTSD Awareness month, and Sojourn Counseling will be raising funds to expand our reach in the community and shine a light on PTSD awareness. Help us reach our financial goal and support those in our community who are affected by PTSD.",
    image: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop&auto=format&q=80',
    heroImage: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1200&h=400&fit=crop&auto=format&q=80',
    websiteUrl: 'https://example.com/event',
    eventTypes: ['Running', 'Walking'],
    distance: ['5K'],
    details: ['Street', 'Sidewalk'],
    galleryStatus: 'Pending',
    raceResultsStatus: 'Available',
    raceResults: [{
      place: 1,
      name: 'John Smith',
      time: '18:32',
      pace: '5:58/mi'
    }, {
      place: 2,
      name: 'Sarah Johnson',
      time: '19:45',
      pace: '6:22/mi'
    }, {
      place: 3,
      name: 'Michael Chen',
      time: '20:12',
      pace: '6:31/mi'
    }, {
      place: 4,
      name: 'Emily Davis',
      time: '21:03',
      pace: '6:47/mi'
    }, {
      place: 5,
      name: 'David Martinez',
      time: '21:28',
      pace: '6:55/mi'
    }],
    coordinates: {
      lat: 33.4951,
      lng: -86.8027
    }
  }, {
    id: 2,
    title: 'City Run Challenge 2024',
    date: '2024-03-15',
    endDate: '2024-03-15',
    displayDate: '03-15-2024 - 07:00 am',
    displayEndDate: '03-15-2024 - 12:00 pm',
    location: 'San Francisco, CA',
    venue: 'Golden Gate Park',
    venueAddress: 'Golden Gate Park, San Francisco, CA 94122, United States',
    organizer: 'SF Running Club',
    description: 'Join us for the annual City Run Challenge through the beautiful streets of San Francisco. This event features multiple distance options and is perfect for runners of all levels.',
    image: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=800&h=600&fit=crop&auto=format&q=80',
    heroImage: 'https://images.unsplash.com/photo-1483721310020-03333e577078?w=1200&h=400&fit=crop&auto=format&q=80',
    websiteUrl: 'https://example.com/city-run',
    eventTypes: ['Running'],
    distance: ['5K', '10K', 'Half Marathon'],
    details: ['Road', 'Urban'],
    galleryStatus: 'Available',
    galleryImages: ['https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1483721310020-03333e577078?w=400&h=300&fit=crop&auto=format&q=80'],
    raceResultsStatus: 'Pending',
    comments: [{
      id: 1,
      author: 'Jennifer Martinez',
      date: '2024-03-16',
      text: "Amazing event! The course was well-organized and the views were spectacular. Can't wait for next year!"
    }, {
      id: 2,
      author: 'Tom Wilson',
      date: '2024-03-16',
      text: 'Great race with excellent support stations. The volunteers were fantastic and really kept us motivated throughout.'
    }, {
      id: 3,
      author: 'Lisa Chen',
      date: '2024-03-17',
      text: 'This was my first half marathon and it was an incredible experience. The atmosphere was so supportive and encouraging!'
    }],
    coordinates: {
      lat: 37.7694,
      lng: -122.4862
    }
  }, {
    id: 3,
    title: 'Coastal Half Marathon',
    date: '2024-03-22',
    endDate: '2024-03-22',
    displayDate: '03-22-2024 - 06:30 am',
    displayEndDate: '03-22-2024 - 11:00 am',
    location: 'San Diego, CA',
    venue: 'Mission Bay Park',
    venueAddress: 'Mission Bay Park, San Diego, CA 92109, United States',
    organizer: 'Coastal Runners',
    description: 'Experience breathtaking ocean views on this scenic half marathon course along the San Diego coastline. Perfect weather and stunning scenery await!',
    image: 'https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=800&h=600&fit=crop&auto=format&q=80',
    heroImage: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=1200&h=400&fit=crop&auto=format&q=80',
    websiteUrl: 'https://example.com/coastal-half',
    eventTypes: ['Running'],
    distance: ['Half Marathon'],
    details: ['Road', 'Coastal'],
    galleryStatus: 'Available',
    galleryImages: ['https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=400&h=300&fit=crop&auto=format&q=80', 'https://images.unsplash.com/photo-1502904550040-7534597429ae?w=400&h=300&fit=crop&auto=format&q=80'],
    raceResultsStatus: 'Pending',
    comments: [{
      id: 1,
      author: 'Mark Johnson',
      date: '2024-03-23',
      text: "The coastal views made this one of the most beautiful races I've ever run. Highly recommend!"
    }, {
      id: 2,
      author: 'Amanda Rodriguez',
      date: '2024-03-23',
      text: 'Perfect weather and a well-marked course. The post-race refreshments were excellent too!'
    }],
    coordinates: {
      lat: 32.7767,
      lng: -117.2263
    }
  }, {
    id: 4,
    title: 'Mountain Trail Ultra',
    date: '2024-04-05',
    endDate: '2024-04-05',
    displayDate: '04-05-2024 - 05:00 am',
    displayEndDate: '04-05-2024 - 06:00 pm',
    location: 'Denver, CO',
    venue: 'Rocky Mountain National Park',
    venueAddress: 'Rocky Mountain National Park, Estes Park, CO 80517, United States',
    organizer: 'Mountain Runners Association',
    description: 'Challenge yourself with this demanding ultra marathon through the stunning Rocky Mountains. Elevation gain, technical terrain, and incredible views!',
    image: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop&auto=format&q=80',
    heroImage: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&h=400&fit=crop&auto=format&q=80',
    websiteUrl: 'https://example.com/mountain-ultra',
    eventTypes: ['Running', 'Trail'],
    distance: ['Ultra Marathon'],
    details: ['Trail', 'Mountain'],
    galleryStatus: 'Pending',
    raceResultsStatus: 'Available',
    raceResults: [{
      place: 1,
      name: 'Alex Thompson',
      time: '7:23:15',
      pace: '8:52/mi'
    }, {
      place: 2,
      name: 'Jessica Lee',
      time: '7:45:32',
      pace: '9:18/mi'
    }, {
      place: 3,
      name: 'Robert Wilson',
      time: '8:12:08',
      pace: '9:50/mi'
    }, {
      place: 4,
      name: 'Maria Garcia',
      time: '8:34:22',
      pace: '10:16/mi'
    }, {
      place: 5,
      name: 'Chris Anderson',
      time: '8:56:45',
      pace: '10:43/mi'
    }],
    coordinates: {
      lat: 40.3428,
      lng: -105.6836
    }
  }];
  const event = allEvents.find(e => e.id === Number(id));
  const relatedEvents = allEvents.filter(e => e.id !== Number(id)).slice(0, 3);
  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !event || mapInstanceRef.current) return;
    const existingLink = document.querySelector('link[href*="leaflet.css"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }
    if (window.L) {
      initializeMap();
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.async = true;
      script.onload = () => initializeMap();
      script.onerror = () => setMapLoaded(true);
      document.head.appendChild(script);
    }
    function initializeMap() {
      if (!mapRef.current || mapInstanceRef.current || !window.L || !event) return;
      try {
        const L = window.L;
        const map = L.map(mapRef.current, {
          center: [event.coordinates.lat, event.coordinates.lng],
          zoom: 13,
          zoomControl: true,
          scrollWheelZoom: false
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);
        const markerIcon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: 40px;
            height: 40px;
            background: #01AAE7;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          </div>`,
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });
        L.marker([event.coordinates.lat, event.coordinates.lng], {
          icon: markerIcon
        }).addTo(map).bindPopup(`<strong>${event.venue}</strong><br/>${event.location}`);
        mapInstanceRef.current = map;
        setMapLoaded(true);
        setTimeout(() => map.invalidateSize(), 100);
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapLoaded(true);
      }
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [event]);
  if (!event) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-937">
        <div className="text-center" data-id="element-938">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-939">
            Event Not Found
          </h2>
          <Link to="/events" className="text-[#01AAE7] hover:text-[#0199d6] font-semibold" data-id="element-940">
            Back to Events
          </Link>
        </div>
      </div>;
  }
  return <div className="w-full bg-white min-h-screen" data-id="element-941">
      {/* Breadcrumb Navigation */}
      <div className="bg-gray-50 border-b border-gray-200" data-id="element-942">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4" data-id="element-943">
          <Link to="/events" className="inline-flex items-center gap-2 text-gray-600 hover:text-[#01AAE7] transition-colors group" data-id="element-944">
            <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-945" />
            <span className="text-sm font-semibold" data-id="element-946">Back to Events</span>
          </Link>
        </div>
      </div>

      {/* Main Content - 70/30 Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-id="element-947">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-948">
          {/* Left Column - 70% */}
          <div className="w-full lg:flex-[0_0_calc(70%-1rem)]" data-id="element-949">
            <div className="space-y-6" data-id="element-950">
              {/* Event Header */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-8" data-id="element-951">
                <div className="p-6 md:p-8" data-id="element-952">
                  <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-6" data-id="element-953">
                    {event.title}
                  </h1>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-id="element-954">
                    {/* Start Time */}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50" data-id="element-955">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center flex-shrink-0" data-id="element-956">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-957">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-958" />
                        </svg>
                      </div>
                      <div data-id="element-959">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-960">
                          Start Time
                        </p>
                        <p className="text-sm md:text-base font-bold text-gray-900" data-id="element-961">
                          {event.displayDate.split(' - ')[1]}
                        </p>
                      </div>
                    </div>

                    {/* End Time */}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50" data-id="element-962">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center flex-shrink-0" data-id="element-963">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-964">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-965" />
                        </svg>
                      </div>
                      <div data-id="element-966">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-967">
                          End Time
                        </p>
                        <p className="text-sm md:text-base font-bold text-gray-900" data-id="element-968">
                          {event.displayEndDate.split(' - ')[1]}
                        </p>
                      </div>
                    </div>

                    {/* Organizer */}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50" data-id="element-969">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center flex-shrink-0" data-id="element-970">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-971">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-972" />
                        </svg>
                      </div>
                      <div data-id="element-973">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-974">
                          Organizer
                        </p>
                        <p className="text-sm md:text-base font-bold text-gray-900" data-id="element-975">
                          {event.organizer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Image with Date and Location Inside */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl border-2 border-gray-100 aspect-[16/7] bg-gray-900" data-id="element-976">
                <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-977" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-978"></div>

                {/* Date and Location Badges - Inside Image */}
                <div className="absolute bottom-6 left-6 right-6" data-id="element-979">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-980">
                    <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg" data-id="element-981">
                      <Calendar className="h-4 w-4 text-[#01AAE7]" data-id="element-982" />
                      <span className="text-sm font-bold text-gray-900" data-id="element-983">
                        {event.displayDate.split(' - ')[0]}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg" data-id="element-984">
                      <MapPin className="h-4 w-4 text-[#01AAE7]" data-id="element-985" />
                      <span className="text-sm font-bold text-gray-900" data-id="element-986">
                        {event.location}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* About This Event */}
              <section data-id="element-987">
                <h2 className="text-2xl font-black text-gray-900 mb-6" data-id="element-988">
                  About This Event
                </h2>
                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-200 relative overflow-hidden" data-id="element-989">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#01AAE7] to-[#0199d6]" data-id="element-990"></div>
                  <div className="pl-6" data-id="element-991">
                    <p className="text-gray-800 text-base leading-relaxed whitespace-pre-line" data-id="element-992">
                      {event.description}
                    </p>
                  </div>
                </div>
              </section>

              {/* Event Details */}
              <section data-id="element-993">
                <h2 className="text-2xl font-black text-gray-900 mb-6" data-id="element-994">
                  Event Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-995">
                  {/* Event Type Card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-996">
                    <div className="flex items-center gap-3 mb-4" data-id="element-997">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center" data-id="element-998">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-999">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" data-id="element-1000" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1001">
                        Event Type
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1002">
                      {event.eventTypes.map(type => <span key={type} className="inline-block bg-blue-50 text-[#01AAE7] text-sm font-bold px-4 py-2 rounded-lg border border-blue-100" data-id="element-1003">
                          {type}
                        </span>)}
                    </div>
                  </div>

                  {/* Distance Card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-1004">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1005">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center" data-id="element-1006">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1007">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" data-id="element-1008" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1009">
                        Distance
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1010">
                      {event.distance.map(dist => <span key={dist} className="inline-block bg-blue-50 text-[#01AAE7] text-sm font-bold px-4 py-2 rounded-lg border border-blue-100" data-id="element-1011">
                          {dist}
                        </span>)}
                    </div>
                  </div>

                  {/* Course Details Card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow md:col-span-2" data-id="element-1012">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1013">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center" data-id="element-1014">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1015">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" data-id="element-1016" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1017">
                        Course Details
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1018">
                      {event.details.map(detail => <span key={detail} className="inline-block bg-blue-50 text-[#01AAE7] text-sm font-bold px-4 py-2 rounded-lg border border-blue-100" data-id="element-1019">
                          {detail}
                        </span>)}
                    </div>
                  </div>

                  {/* Gallery Card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow md:col-span-2" data-id="element-1020">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1021">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center" data-id="element-1022">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1023">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" data-id="element-1024" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1025">
                        Gallery
                      </h3>
                    </div>
                    {event.galleryImages && event.galleryImages.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1026">
                        {event.galleryImages.map((img, index) => <div key={index} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 group cursor-pointer" data-id="element-1027">
                            <img src={img} alt={`Gallery image ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" data-id="element-1028" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" data-id="element-1029"></div>
                          </div>)}
                      </div> : <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1030">
                        <span className="text-sm font-semibold text-gray-600" data-id="element-1031">
                          {event.galleryStatus}
                        </span>
                      </div>}
                  </div>
                </div>
              </section>

              {/* Race Results */}
              <section data-id="element-1032">
                <h2 className="text-2xl font-black text-gray-900 mb-6" data-id="element-1033">
                  Race Results
                </h2>
                {event.raceResults && event.raceResults.length > 0 ? <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden" style={{
                backgroundColor: '#FCFCFD'
              }} data-id="element-1034">
                    <div className="overflow-x-auto" data-id="element-1035">
                      <table className="w-full" data-id="element-1036">
                        <thead className="bg-gradient-to-r from-[#0E578E] to-[#01AAE7] text-white" data-id="element-1037">
                          <tr data-id="element-1038">
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider" data-id="element-1039">
                              Place
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider" data-id="element-1040">
                              Name
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider" data-id="element-1041">
                              Time
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider" data-id="element-1042">
                              Pace
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-1043">
                          {event.raceResults.map((result, index) => <tr key={index} className="hover:bg-gray-50 transition-colors" data-id="element-1044">
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1045">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white text-sm font-bold" data-id="element-1046">
                                  {result.place}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1047">
                                {result.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700" data-id="element-1048">
                                {result.time}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-1049">
                                {result.pace}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div> : <span className="inline-block bg-gray-100 text-gray-700 text-sm font-bold px-4 py-2 rounded-lg" data-id="element-1050">
                    {event.raceResultsStatus}
                  </span>}
              </section>

              {/* Comments */}
              <section className="bg-gray-50 rounded-xl p-8 border border-gray-200" data-id="element-1051">
                <h2 className="text-2xl font-black text-gray-900 mb-6" data-id="element-1052">
                  Comments
                </h2>
                {event.comments && event.comments.length > 0 ? <div className="space-y-6" data-id="element-1053">
                    {event.comments.map(comment => <div key={comment.id} className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-1054">
                        <div className="flex items-start gap-4" data-id="element-1055">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#01AAE7] to-[#0199d6] flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1056">
                            {comment.author.charAt(0)}
                          </div>
                          <div className="flex-1" data-id="element-1057">
                            <div className="flex items-center justify-between mb-2" data-id="element-1058">
                              <h4 className="font-bold text-gray-900" data-id="element-1059">
                                {comment.author}
                              </h4>
                              <span className="text-xs text-gray-500" data-id="element-1060">
                                {comment.date}
                              </span>
                            </div>
                            <p className="text-gray-700 leading-relaxed" data-id="element-1061">
                              {comment.text}
                            </p>
                          </div>
                        </div>
                      </div>)}
                    <div className="pt-6 border-t border-gray-200" data-id="element-1062">
                      <button className="w-full px-8 py-3 bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-1063">
                        Add Comment
                      </button>
                    </div>
                  </div> : <div className="text-center py-8" data-id="element-1064">
                    <p className="text-gray-600 mb-6" data-id="element-1065">
                      No comments yet. Be the first to share your thoughts!
                    </p>
                    <button className="px-8 py-3 bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-1066">
                      Login to Comment
                    </button>
                  </div>}
              </section>
            </div>
          </div>

          {/* Right Column - 30% */}
          <div className="w-full lg:flex-[0_0_calc(30%-1rem)]" data-id="element-1067">
            <div className="space-y-6 lg:sticky lg:top-6" data-id="element-1068">
              {/* Event Location (Map) */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100" data-id="element-1069">
                <div className="p-4 bg-gradient-to-r from-[#0E578E] to-[#01AAE7]" data-id="element-1070">
                  <h3 className="text-lg font-bold text-white" data-id="element-1071">
                    Event Location
                  </h3>
                </div>
                <div className="relative w-full h-[300px]" data-id="element-1072">
                  <div ref={mapRef} className="absolute inset-0 w-full h-full" data-id="element-1073"></div>
                  {!mapLoaded && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10" data-id="element-1074">
                      <div className="text-center" data-id="element-1075">
                        <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3 animate-pulse" data-id="element-1076" />
                        <p className="text-sm text-gray-600 font-medium" data-id="element-1077">
                          Loading map...
                        </p>
                      </div>
                    </div>}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100" style={{
              backgroundColor: '#FCFCFD'
            }} data-id="element-1078">
                <h3 className="text-lg font-black text-gray-900 mb-4" data-id="element-1079">
                  Quick Actions
                </h3>
                <div className="space-y-3" data-id="element-1080">
                  <a href={event.websiteUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold py-3 rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm" data-id="element-1081">
                    <ExternalLink className="h-4 w-4" data-id="element-1082" />
                    Event Website
                  </a>
                  <button className="flex items-center justify-center gap-2 w-full bg-white border-2 border-[#01AAE7] text-[#01AAE7] font-bold py-3 rounded-xl hover:bg-[#01AAE7] hover:text-white transition-all text-sm" data-id="element-1083">
                    <Download className="h-4 w-4" data-id="element-1084" />
                    Add to Calendar
                  </button>
                  <button className="flex items-center justify-center gap-2 w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all text-sm" data-id="element-1085">
                    <Share2 className="h-4 w-4" data-id="element-1086" />
                    Share Event
                  </button>
                  <button className="flex items-center justify-center gap-2 w-full bg-red-50 border-2 border-red-200 text-red-600 font-bold py-3 rounded-xl hover:bg-red-100 transition-all text-sm" data-id="element-1087">
                    <AlertCircle className="h-4 w-4" data-id="element-1088" />
                    Report Problem
                  </button>
                </div>
              </div>

              {/* Venue */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100" style={{
              backgroundColor: '#FCFCFD'
            }} data-id="element-1089">
                <h3 className="text-lg font-black text-gray-900 mb-4" data-id="element-1090">Venue</h3>
                <p className="text-sm font-bold text-gray-900 mb-2" data-id="element-1091">
                  {event.venue}
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mb-6" data-id="element-1092">
                  {event.venueAddress}
                </p>

                <div className="pt-6 border-t border-gray-200" data-id="element-1093">
                  <h4 className="text-xs font-bold text-gray-500 uppercase mb-3" data-id="element-1094">
                    Follow Us
                  </h4>
                  <div className="flex gap-3" data-id="element-1095">
                    <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#0E578E] to-[#01AAE7] rounded-lg flex items-center justify-center hover:scale-110 transition-transform shadow-md hover:shadow-lg" data-id="element-1096">
                      <Instagram className="h-5 w-5 text-white" data-id="element-1097" />
                    </a>
                    <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#0E578E] to-[#01AAE7] rounded-lg flex items-center justify-center hover:scale-110 transition-transform shadow-md hover:shadow-lg" data-id="element-1098">
                      <Facebook className="h-5 w-5 text-white" data-id="element-1099" />
                    </a>
                    <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#0E578E] to-[#01AAE7] rounded-lg flex items-center justify-center hover:scale-110 transition-transform shadow-md hover:shadow-lg" data-id="element-1100">
                      <Twitter className="h-5 w-5 text-white" data-id="element-1101" />
                    </a>
                  </div>
                </div>
              </div>

              {/* You May Also Like */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100" style={{
              backgroundColor: '#FCFCFD'
            }} data-id="element-1102">
                <h3 className="text-lg font-black text-gray-900 mb-4" data-id="element-1103">
                  You May Also Like
                </h3>
                <div className="space-y-4" data-id="element-1104">
                  {relatedEvents.map(relatedEvent => <Link key={relatedEvent.id} to={`/events/${relatedEvent.id}`} className="block group" data-id="element-1105">
                      <div className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1106">
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-100" data-id="element-1107">
                          <img src={relatedEvent.image} alt={relatedEvent.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" data-id="element-1108" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1109">
                          <h4 className="font-bold text-sm text-gray-900 group-hover:text-[#01AAE7] transition-colors line-clamp-2 mb-1 leading-tight" data-id="element-1110">
                            {relatedEvent.title}
                          </h4>
                          <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1111">
                            <Calendar className="h-3 w-3" data-id="element-1112" />
                            {relatedEvent.displayDate.split(' - ')[0]}
                          </p>
                        </div>
                      </div>
                    </Link>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}